import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'

export type InstanceStatus = 'up' | 'down'
export type TaskStatus = 'new' | 'in_progress' | 'completed' | 'failed'
export type TaskType =
  | 'up'
  | 'down'
  | 'restart_services'
  | 'upgrade_services'
  | 'update_pjsip'
  | 'sync_voices'
export type StackType = 'asterisk' | 'controller'

// Simplified serialized instance type (dates become strings after JSON serialization)
export interface SerializedTask {
  id: number
  instanceDbId: number
  taskType: TaskType
  taskParams: Record<string, unknown> | null
  status: TaskStatus
  output: string | null
  createdAt: Date | string
  startedAt: Date | string | null
  completedAt: Date | string | null
}

export interface SerializedInstance {
  id: number
  campaignId: number
  stack: StackType
  instanceId: string | null
  publicIp: string | null
  privateIp: string | null
  elasticIp: string | null
  elasticIpAllocationId: string | null
  instanceSize: string
  requiresElasticIp: boolean
  status: InstanceStatus
  params: Record<string, unknown>
  createdAt: Date | string
  updatedAt: Date | string
  lastTask: SerializedTask | null
}

interface InstanceInfoCardProps {
  instance: SerializedInstance | null
  title: string
}

function formatDate(date: Date | string | null): string {
  if (!date) return '-'
  return new Date(date).toLocaleString()
}

function getStatusBadge(status: 'up' | 'down') {
  if (status === 'up') {
    return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Up</Badge>
  }
  return (
    <Badge variant="secondary" className="bg-gray-100 text-gray-800">
      Down
    </Badge>
  )
}

function getTaskStatusBadge(status: string) {
  switch (status) {
    case 'completed':
      return <Badge className="bg-green-100 text-green-800 hover:bg-green-100">Completed</Badge>
    case 'failed':
      return <Badge className="bg-red-100 text-red-800 hover:bg-red-100">Failed</Badge>
    case 'in_progress':
      return <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">In Progress</Badge>
    case 'new':
      return <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">Pending</Badge>
    default:
      return <Badge variant="secondary">{status}</Badge>
  }
}

export function InstanceInfoCard({ instance, title }: InstanceInfoCardProps) {
  if (!instance) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{title} Instance</CardTitle>
          <CardDescription>No instance configured yet</CardDescription>
        </CardHeader>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>{title} Instance</CardTitle>
          {getStatusBadge(instance.status)}
        </div>
        <CardDescription>Instance information and status</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-body">
          <div>
            <p className="text-gray-500">Instance ID</p>
            <p className="font-mono">{instance.instanceId || '-'}</p>
          </div>
          <div>
            <p className="text-gray-500">Public IP</p>
            <p className="font-mono">{instance.publicIp || '-'}</p>
          </div>
          <div>
            <p className="text-gray-500">Private IP</p>
            <p className="font-mono">{instance.privateIp || '-'}</p>
          </div>
          <div>
            <p className="text-gray-500">Size</p>
            <p>{instance.instanceSize}</p>
          </div>
          <div>
            <p className="text-gray-500">Elastic IP</p>
            <p className="font-mono">{instance.elasticIp || '-'}</p>
          </div>
          <div>
            <p className="text-gray-500">Created</p>
            <p>{formatDate(instance.createdAt)}</p>
          </div>
        </div>

        {instance.lastTask && (
          <div className="mt-4 pt-4 border-t">
            <h4 className="text-body font-semibold text-gray-700 mb-2">Last Task</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-body">
              <div>
                <p className="text-gray-500">Type</p>
                <p>{instance.lastTask.taskType.replace('_', ' ')}</p>
              </div>
              <div>
                <p className="text-gray-500">Status</p>
                {getTaskStatusBadge(instance.lastTask.status)}
              </div>
              <div>
                <p className="text-gray-500">Created</p>
                <p>{formatDate(instance.lastTask.createdAt)}</p>
              </div>
              <div>
                <p className="text-gray-500">Completed</p>
                <p>{formatDate(instance.lastTask.completedAt)}</p>
              </div>
            </div>
            {instance.lastTask.status === 'failed' && instance.lastTask.output && (
              <div className="mt-2 p-2 bg-red-50 rounded text-detail text-red-800 font-mono overflow-auto max-h-32">
                {instance.lastTask.output.substring(0, 500)}
                {instance.lastTask.output.length > 500 && '...'}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
